import React, { useEffect, useState } from 'react';
import { Button, Flex, Input, InputNumber, Modal } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

// Phases must sit inside the plan and must not overlap; mirrors server utils/resourceRules.js.
export const checkPhases = (phases, totalWeeks) => {
  if (phases.some((phase) => !phase.name.trim())) return 'Every phase needs a name.';
  if (phases.some((phase) => !phase.startWeek || !phase.endWeek)) return 'Every phase needs a start and end week.';
  const sorted = [...phases].sort((a, b) => a.startWeek - b.startWeek);
  for (let i = 0; i < sorted.length; i++) {
    const phase = sorted[i];
    if (phase.endWeek < phase.startWeek) return `"${phase.name}" ends before it starts.`;
    if (phase.endWeek > totalWeeks) return `"${phase.name}" ends after week ${totalWeeks}.`;
    if (i > 0 && phase.startWeek <= sorted[i - 1].endWeek) {
      return `"${sorted[i - 1].name}" and "${phase.name}" overlap.`;
    }
  }
  return null;
};

const PhaseEditor = ({ open, phases, totalWeeks, onCancel, onApply }) => {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (open) setRows(phases.map((phase) => ({ ...phase })));
  }, [open, phases]);

  const update = (index, field, value) =>
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));

  const addPhase = () => {
    const lastEnd = rows.reduce((max, row) => Math.max(max, row.endWeek || 0), 0);
    const start = Math.min(lastEnd + 1, totalWeeks);
    setRows((prev) => [
      ...prev,
      { name: `Sprint ${prev.length + 1}`, startWeek: start, endWeek: Math.min(start + 1, totalWeeks) },
    ]);
  };

  const error = checkPhases(rows, totalWeeks);

  return (
    <Modal
      title="Sprints & phases"
      open={open}
      onCancel={onCancel}
      okText="Apply"
      okButtonProps={{ disabled: !!error }}
      onOk={() =>
        onApply([...rows].sort((a, b) => a.startWeek - b.startWeek).map((row) => ({ ...row, name: row.name.trim() })))
      }
      width={560}
    >
      <Flex vertical gap={12}>
        <span style={{ color: '#475467' }}>
          Each phase covers a range of weeks (W1–W{totalWeeks}). Weeks outside any phase stay unlabelled.
        </span>
        {rows.length > 0 && (
          <Flex gap={8} style={{ fontSize: 12, color: '#5D6679' }}>
            <span style={{ flex: 1 }}>Name</span>
            <span style={{ width: 96 }}>From week</span>
            <span style={{ width: 96 }}>To week</span>
            <span style={{ width: 32 }} />
          </Flex>
        )}
        {rows.map((row, index) => (
          <Flex gap={8} key={index} align="center">
            <Input
              aria-label="Phase name"
              style={{ flex: 1 }}
              maxLength={60}
              value={row.name}
              onChange={(e) => update(index, 'name', e.target.value)}
            />
            <InputNumber
              aria-label="From week"
              style={{ width: 96 }}
              min={1}
              max={totalWeeks}
              prefix="W"
              value={row.startWeek}
              onChange={(value) => update(index, 'startWeek', value)}
            />
            <InputNumber
              aria-label="To week"
              style={{ width: 96 }}
              min={1}
              max={totalWeeks}
              prefix="W"
              value={row.endWeek}
              onChange={(value) => update(index, 'endWeek', value)}
            />
            <Button
              type="text"
              aria-label="Remove phase"
              icon={<DeleteOutlined />}
              onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
            />
          </Flex>
        ))}
        <Button icon={<PlusOutlined />} onClick={addPhase} disabled={rows.length >= totalWeeks}>
          Add phase
        </Button>
        {error && rows.length > 0 && <span style={{ color: '#D92D20' }}>{error}</span>}
      </Flex>
    </Modal>
  );
};

export default PhaseEditor;
